import { create } from "zustand";

export interface UserSettings {
  dateSystem: string | null;
  roundPrecision: number | null;
  numberFormattingLocale: string;
  chartPrimaryColor: string | null;
}

export interface AppUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role_slug: string;
  tutorial_advancement?: any;
}

export interface AppState {
  // User
  currentUser: AppUser | null;
  userSettings: UserSettings;
  commercialPlan: any | null;
  hasErpConnections: boolean;

  // UI controls
  errorSnackBarOpen: boolean;
  forbiddenSnackBarOpen: boolean;
  backdropOpen: boolean;
  shouldInit: number;
}

export interface AppActions {
  setCurrentUser: (user: AppUser | null) => void;
  setUserSettings: (settings: Partial<UserSettings>) => void;
  setCommercialPlan: (plan: any | null) => void;
  decrementModelsRemaining: () => void;
  incrementModelsRemaining: () => void;
  setHasErpConnections: (value: boolean) => void;
  openErrorSnackBar: () => void;
  closeErrorSnackBar: () => void;
  openForbiddenSnackBar: () => void;
  closeForbiddenSnackBar: () => void;
  openBackdrop: () => void;
  closeBackdrop: () => void;
  triggerInit: () => void;
}

export type AppStore = AppState & AppActions;

const DEFAULT_USER_SETTINGS: UserSettings = {
  dateSystem: null,
  roundPrecision: null,
  numberFormattingLocale: "en-GB",
  chartPrimaryColor: null,
};

export const useAppStore = create<AppStore>((set) => ({
  // State
  currentUser: null,
  userSettings: { ...DEFAULT_USER_SETTINGS },
  commercialPlan: null,
  hasErpConnections: false,
  errorSnackBarOpen: false,
  forbiddenSnackBarOpen: false,
  backdropOpen: false,
  shouldInit: 0,

  // Actions
  setCurrentUser: (user) => set({ currentUser: user }),

  setUserSettings: (settings) =>
    set((state) => ({
      userSettings: { ...state.userSettings, ...settings },
    })),

  setCommercialPlan: (plan) => set({ commercialPlan: plan }),

  decrementModelsRemaining: () =>
    set((state) => {
      if (!state.commercialPlan || state.commercialPlan.models_remaining_count === -1) return state;
      return {
        commercialPlan: {
          ...state.commercialPlan,
          models_remaining_count: state.commercialPlan.models_remaining_count - 1,
        },
      };
    }),

  incrementModelsRemaining: () =>
    set((state) => {
      if (!state.commercialPlan || state.commercialPlan.models_remaining_count === -1) return state;
      return {
        commercialPlan: {
          ...state.commercialPlan,
          models_remaining_count: state.commercialPlan.models_remaining_count + 1,
        },
      };
    }),

  setHasErpConnections: (value) => set({ hasErpConnections: value }),

  openErrorSnackBar: () => set({ errorSnackBarOpen: true }),
  closeErrorSnackBar: () => set({ errorSnackBarOpen: false }),

  openForbiddenSnackBar: () => set({ forbiddenSnackBarOpen: true }),
  closeForbiddenSnackBar: () => set({ forbiddenSnackBarOpen: false }),

  openBackdrop: () => set({ backdropOpen: true }),
  closeBackdrop: () => set({ backdropOpen: false }),

  triggerInit: () =>
    set((state) => ({ shouldInit: state.shouldInit + 1 })),
}));
